import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { ChevronDown, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { de, type Trip } from "@ainkauf/core";
import { BottomNav } from "@/components/BottomNav";
import { allStores, allTrips, defaultList, itemsOfTrip, repeatTrip } from "@/db/repository";
import { useNavigate } from "@/lib/router";

const euro = (cents: number) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
const datum = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "long", year: "numeric" });

/** Abgeschlossene Einkäufe, neueste zuerst, mit Positionen zum Aufklappen. */
export function HistoryScreen() {
  const go = useNavigate();
  const trips = useLiveQuery(() => allTrips(), [], []);
  const stores = useLiveQuery(() => allStores(), [], []);
  const [offen, setOffen] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  const ladenName = (trip: Trip) => stores.find((store) => store.id === trip.store_id)?.name ?? null;

  async function wiederholen(tripId: string) {
    const liste = await defaultList();
    if (!liste) return;
    const anzahl = await repeatTrip(tripId, liste.id);
    setMeldung(de.trip.repeated(anzahl));
    window.setTimeout(() => {
      setMeldung(null);
    }, 4000);
  }

  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-4 px-4 mit-leiste">
      <header className="flex items-center gap-2 pt-5">
        <button
          type="button"
          onClick={() => {
            go("/ausgaben");
          }}
          aria-label={de.common.back}
          className="inline-flex size-11 items-center justify-center rounded-full bg-card text-ink-2 shadow-[var(--shadow-card)]"
        >
          <ChevronLeft size={22} strokeWidth={1.5} aria-hidden />
        </button>
        <h1 className="text-[2rem]">{de.history.title}</h1>
      </header>

      {trips.length === 0 ? (
        <p className="karte px-5 py-10 text-center text-ink-2">{de.history.empty}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {trips.map((trip) => (
            <li key={trip.id} className="karte overflow-hidden">
              <button
                type="button"
                onClick={() => {
                  setOffen(offen === trip.id ? null : trip.id);
                }}
                aria-expanded={offen === trip.id}
                data-testid="trip-row"
                className="flex min-h-[72px] w-full items-center gap-3 px-4 text-left"
              >
                {offen === trip.id ? (
                  <ChevronDown size={18} strokeWidth={1.5} className="shrink-0 text-ink-3" aria-hidden />
                ) : (
                  <ChevronRight size={18} strokeWidth={1.5} className="shrink-0 text-ink-3" aria-hidden />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-display text-lg font-extrabold tracking-tight text-ink">{datum.format(new Date(trip.finished_at ?? trip.started_at))}</span>
                  <span className="block truncate text-sm text-ink-2">{ladenName(trip) ?? de.lists.noStore}</span>
                </span>
                <span className="num shrink-0 text-ink-2">
                  {trip.total_cents === null ? de.history.noAmount : euro(trip.total_cents)}
                </span>
              </button>

              {offen === trip.id && <TripDetail tripId={trip.id} onRepeat={() => void wiederholen(trip.id)} />}
            </li>
          ))}
        </ul>
      )}

      {meldung !== null && (
        <p role="status" data-testid="history-toast" className="rounded-full bg-herb-soft px-4 py-3 text-center font-semibold text-herb">
          {meldung}
        </p>
      )}

      <BottomNav active="ausgaben" />
    </div>
  );
}

function TripDetail({ tripId, onRepeat }: { tripId: string; onRepeat: () => void }) {
  const positionen = useLiveQuery(() => itemsOfTrip(tripId), [tripId], []);

  return (
    <div className="flex flex-col gap-2 border-t border-paper-2 px-4 py-3">
      <span className="text-sm text-ink-2">{de.history.positions(positionen.length)}</span>
      <ul className="flex flex-col">
        {positionen.map((position) => (
          <li key={position.id} className="flex min-h-[36px] items-center justify-between gap-3 text-sm">
            <span className="truncate">{position.name}</span>
            <span className="num shrink-0 text-ink-2">
              {position.quantity !== null ? `${position.quantity}${position.unit ? ` ${position.unit}` : ""}` : ""}
              {position.price_cents !== null ? ` · ${euro(position.price_cents)}` : ""}
            </span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onRepeat}
        data-testid="repeat-trip"
        className="knopf-leise inline-flex min-h-[48px] items-center justify-center gap-2 px-4"
      >
        <RotateCcw size={18} strokeWidth={1.5} aria-hidden />
        {de.trip.repeat}
      </button>
    </div>
  );
}
